import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Alert,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PersonIcon from "@mui/icons-material/Person";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = "America/St_Johns";

const ADDONS = [
  { label: "Loot Bags", key: "lootBags", pricePerKid: 4 },
  { label: "Juice & Water", key: "juiceWater", pricePerKid: 3 },
  { label: "Kid Food", key: "kidFood", pricePerKid: 7 },
  { label: "Parent Snacks", key: "parentSnacks", pricePerKid: 5 },
  { label: "Coffee/Tea", key: "coffee", flat: 20 },
  { label: "Custom Birthday Cake", key: "cake", flat: 80 },
];

const BASE_PRICE_STANDARD = 325;
const EXTRA_KID_COST = 30;
const TAX_RATE = 0.15;

const ReviewSubmitStep = ({ bookingData, submitted }) => {
  const {
    package: pkg,
    options = {},
    date,
    timeSlot,
    contact = {},
  } = bookingData;

  const numKids = options.numKids ?? 12;

  const selectedAddons = useMemo(() => {
    return ADDONS.filter((a) => options[a.key]);
  }, [options]);

  const { subtotal, tax, total, addonBreakdown } = useMemo(() => {
    if (pkg !== "standard")
      return { subtotal: 0, tax: 0, total: 0, addonBreakdown: [] };

    const extraKids = Math.max(0, numKids - 12);
    let base = BASE_PRICE_STANDARD + extraKids * EXTRA_KID_COST;

    const addonBreakdown = selectedAddons.map((addon) => {
      const price = addon.flat ? addon.flat : addon.pricePerKid * numKids;
      return {
        label: addon.label,
        value: price,
        detail: addon.flat
          ? `Flat $${addon.flat}`
          : `$${addon.pricePerKid} × ${numKids}`,
      };
    });

    const addonsTotal = addonBreakdown.reduce((sum, a) => sum + a.value, 0);
    const subtotal = base + addonsTotal;
    const tax = subtotal * TAX_RATE;
    const total = subtotal + tax;

    return { subtotal, tax, total, addonBreakdown };
  }, [pkg, numKids, selectedAddons]);

  const formattedDate = date
    ? dayjs(date).tz(TIMEZONE).format("dddd, MMMM D, YYYY")
    : null;

  return (
    <Box>
      {submitted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          🎉 Booking submitted! We’ll contact you shortly to confirm.
        </Alert>
      )}

      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Final Review
      </Typography>
      <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
        Review your booking details before submitting.
      </Typography>

      {/* Package Info */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <LocalOfferIcon sx={{ mr: 1, color: "#FF6F61" }} />
          <Typography variant="h6">Package</Typography>
        </Box>
        <List dense>
          <ListItem>
            <ListItemText
              primary={
                pkg === "standard" ? "Standard Package" : "Premium Package"
              }
            />
          </ListItem>

          {pkg === "standard" && (
            <>
              <ListItem>
                <ListItemText
                  primary="Number of Kids"
                  secondary={`${numKids} kids`}
                />
              </ListItem>

              {addonBreakdown.map((a) => (
                <ListItem key={a.label}>
                  <ListItemText
                    primary={a.label}
                    secondary={`${a.detail} → $${a.value.toFixed(2)}`}
                  />
                </ListItem>
              ))}

              {numKids > 12 && (
                <ListItem>
                  <ListItemText
                    primary="Extra Kids Fee"
                    secondary={`$${EXTRA_KID_COST} × ${numKids - 12} = $${(
                      EXTRA_KID_COST *
                      (numKids - 12)
                    ).toFixed(2)}`}
                  />
                </ListItem>
              )}

              {options.notes && (
                <ListItem>
                  <ListItemText primary="Notes" secondary={options.notes} />
                </ListItem>
              )}
            </>
          )}

          {pkg === "premium" && (
            <>
              <ListItem>
                <ListItemText
                  primary="Parent Treat"
                  secondary={
                    options.parentTreat === "fruit"
                      ? "Fruit Cups"
                      : "Charcuterie Cups"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Cutlery Color"
                  secondary={options.cutleryColor}
                />
              </ListItem>
              {options.notes && (
                <ListItem>
                  <ListItemText primary="Notes" secondary={options.notes} />
                </ListItem>
              )}
            </>
          )}
        </List>
      </Paper>

      {/* Date & Time */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <CalendarMonthIcon sx={{ mr: 1, color: "#FF6F61" }} />
          <Typography variant="h6">Date & Time</Typography>
        </Box>
        <List dense>
          <ListItem>
            <ListItemText
              primary={`Date: ${formattedDate || "Not selected"}`}
            />
          </ListItem>
          <ListItem>
            <ListItemText primary={`Time Slot: ${timeSlot}`} />
          </ListItem>
        </List>
      </Paper>

      {/* Contact Info */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: "#FF6F61" }} />
          <Typography variant="h6">Contact Info</Typography>
        </Box>
        <List dense>
          <ListItem>
            <ListItemText
              primary={`${contact.firstName} ${contact.lastName}`}
            />
          </ListItem>
          <ListItem>
            <ListItemText primary={contact.email} />
          </ListItem>
          <ListItem>
            <ListItemText primary={contact.phone} />
          </ListItem>
        </List>
      </Paper>

      {/* Totals */}
      {pkg === "standard" && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Summary
          </Typography>
          <Typography>
            <strong>Subtotal:</strong> ${subtotal.toFixed(2)}
          </Typography>
          <Typography>
            <strong>Tax (15%):</strong> ${tax.toFixed(2)}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="h6">
            <strong>Total:</strong> ${total.toFixed(2)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReviewSubmitStep;
